import daDK from './dictionaries/da-DK.json'
import enUS from './dictionaries/en-US.json'
import type { TranslationDictionary, TranslationParams, Translator } from './types'

export const DEFAULT_UI_LOCALE = 'da-DK'
export const DEFAULT_CV_LOCALE = 'da-DK'
export const SUPPORTED_LOCALES = ['da-DK', 'en-US'] as const

export const defaultDictionaries: Record<string, TranslationDictionary> = {
  'da-DK': daDK as TranslationDictionary,
  'en-US': enUS as TranslationDictionary,
}

export function resolveLocale(
  locale: string | null | undefined,
  dictionaries: Record<string, TranslationDictionary> = defaultDictionaries,
  fallbackLocale = DEFAULT_UI_LOCALE
): string {
  const requestedLocale = locale?.trim()
  if (!requestedLocale) return fallbackLocale
  if (dictionaries[requestedLocale]) return requestedLocale

  const requestedLanguage = requestedLocale.split('-')[0]?.toLowerCase()
  const matchedLocale = Object.keys(dictionaries).find(
    (candidate) => candidate.split('-')[0]?.toLowerCase() === requestedLanguage
  )

  return matchedLocale ?? fallbackLocale
}

export function createTranslator(
  locale: string,
  dictionaries: Record<string, TranslationDictionary> = defaultDictionaries,
  fallbackLocale = DEFAULT_UI_LOCALE
): Translator {
  const resolvedLocale = resolveLocale(locale, dictionaries, fallbackLocale)
  const resolvedFallbackLocale = resolveLocale(fallbackLocale, dictionaries, DEFAULT_UI_LOCALE)

  return (key, params) => {
    const value =
      resolveTranslationValue(dictionaries[resolvedLocale], key) ??
      resolveTranslationValue(dictionaries[resolvedFallbackLocale], key) ??
      key

    return interpolate(value, params)
  }
}

export function formatLocaleDate(
  date: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' }
): string {
  if (!date) return ''

  const normalizedDate = date.length === 7 ? `${date}-01` : date
  const parsedDate = new Date(`${normalizedDate}T00:00:00`)
  if (Number.isNaN(parsedDate.getTime())) return date

  return new Intl.DateTimeFormat(resolveLocale(locale), options).format(parsedDate)
}

function resolveTranslationValue(
  dictionary: TranslationDictionary | undefined,
  key: string
): string | undefined {
  if (!dictionary) return undefined

  let value: string | TranslationDictionary | undefined = dictionary
  for (const part of key.split('.')) {
    if (!value || typeof value === 'string') return undefined
    value = value[part]
  }

  return typeof value === 'string' ? value : undefined
}

function interpolate(value: string, params?: TranslationParams): string {
  if (!params) return value

  return value.replace(/\{(\w+)\}/g, (match, key: string) => {
    const replacement = params[key]
    return replacement === undefined ? match : String(replacement)
  })
}
